import { useMemo } from 'react'

import { match } from 'ts-pattern'
import { isValid, parse } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

type ResolutionBody =
  Dashboardv99990101Api.PostInvoiceReminderVariableResolution.Body

import { ExecutionSummaryComponentProps } from 'Automations/nodeDefinitions/types'
import {
  hasCancelledTask,
  hasFailedTask,
  isTaskInProgress
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'
import { parseTaskReferenceName } from 'Automations/nodeDefinitions/conductor'
import { REMINDER_TASK_NAMES } from './reminder.constants'
import type { Config, TaskName } from '.'

type Task = ExecutionSummaryComponentProps['tasks'][number]

export type ReminderState =
  | { type: 'INVOICE_PAID' }
  | { type: 'INVOICE_UNCOLLECTIBLE' }
  | { type: 'CANCELLED' }
  | { type: 'FAILED' }
  | { type: 'SENT' }
  | { type: 'SENDING' }
  | { type: 'SKIPPED' }
  | { type: 'IN_REVIEW'; isReplacementFailure: boolean }
  | { type: 'WAITING'; waitUntil: Date | null }
  | { type: 'PREPARING' }

export const useReminderData = ({
  tasks,
  automationExecution
}: {
  tasks: ExecutionSummaryComponentProps['tasks']
  automationExecution: ExecutionSummaryComponentProps['automationExecution']
}): {
  state: ReminderState
  config: Config | undefined
  emailData: ReminderEmailData
} => {
  const automationVersionQuery = useQuery(
    dashboardv99990101Client.getAutomationVersion,
    {
      automationId: automationExecution.automationId,
      versionId: automationExecution.automationVersionId
    }
  )

  const config = useMemo(() => {
    const presentation = automationVersionQuery.data?.presentation
    const nodeId = tasks.at(0)
      ? parseTaskReferenceName(tasks[0].taskReferenceName).nodeId
      : undefined
    if (!nodeId || !isAutomationPresentation(presentation)) {
      return undefined
    }

    const reminderNode = presentation.nodes.find(
      node => node.type === 'REMINDER' && node.id === nodeId
    )
    return reminderNode?.data as Config | undefined
  }, [automationVersionQuery.data, tasks])

  const state = useMemo(() => deriveReminderState(tasks), [tasks])

  const emailData = useMemo(() => getReminderEmailData(tasks), [tasks])

  return { state, config, emailData }
}

export type ReviewedEmail = {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body: string
}

export type ReminderEmailData = {
  /** Full invoice object fetched by the workflow, for variable resolution */
  invoice: ResolutionBody['invoice'] | undefined
  /** Full customer object fetched by the workflow, for variable resolution */
  customer: ResolutionBody['customer'] | undefined
  /**
   * The reviewer-edited email from a completed review task — the values that
   * were (or will be) actually sent, taking precedence over the node config
   */
  reviewedEmail: ReviewedEmail | undefined
}

const getReminderEmailData = (
  tasks: ExecutionSummaryComponentProps['tasks']
): ReminderEmailData => {
  const parsedTasks = tasks.map(task => ({
    task,
    parsed: parseTaskReferenceName(task.taskReferenceName)
  }))

  const findTask = (taskName: TaskName) =>
    parsedTasks.find(({ parsed }) => parsed.taskName === taskName)?.task

  const invoice = findTask(REMINDER_TASK_NAMES.getDataInvoice)?.outputData
    .invoice as ResolutionBody['invoice'] | undefined
  const customer = findTask(REMINDER_TASK_NAMES.getDataCustomer)?.outputData
    .customer as ResolutionBody['customer'] | undefined

  const completedReviewTask = [
    findTask(REMINDER_TASK_NAMES.manualReview),
    findTask(REMINDER_TASK_NAMES.replacementFailureManualReview)
  ].find(task => task?.status === 'COMPLETED')
  const reviewOutput = completedReviewTask?.outputData as
    | {
        to?: string[]
        cc?: string[]
        bcc?: string[]
        subject?: string
        text?: string
      }
    | undefined

  const reviewedEmail =
    reviewOutput?.to && reviewOutput.subject !== undefined
      ? {
          to: reviewOutput.to,
          cc: reviewOutput.cc ?? [],
          bcc: reviewOutput.bcc ?? [],
          subject: reviewOutput.subject,
          body: reviewOutput.text ?? ''
        }
      : undefined

  return { invoice, customer, reviewedEmail }
}

// The send task's reference name differs by path: approval flow, no-failure
// flow, and post-replacement-failure-review flow
const SEND_TASK_NAMES: TaskName[] = [
  REMINDER_TASK_NAMES.sendInvoiceReminder,
  REMINDER_TASK_NAMES.sendInvoiceReminderWithoutFailureReview,
  REMINDER_TASK_NAMES.replacementFailureSendInvoiceReminderAfterReview
]

const getInvoicePaymentStatus = (task: Task | undefined): string | undefined =>
  (task?.outputData.invoice as { paymentStatus?: string } | undefined)
    ?.paymentStatus

// Wait tasks carry their `until` timestamp in the form
// `2026-07-26 00:00 GMT+00:00`
const WAIT_UNTIL_FORMAT = "yyyy-MM-dd HH:mm 'GMT'xxx"

const parseWaitUntil = (value: unknown): Date | null => {
  if (typeof value !== 'string') {
    return null
  }

  const parsed = parse(value, WAIT_UNTIL_FORMAT, new Date())
  return isValid(parsed) ? parsed : null
}

/**
 * Maps a reminder node's tasks to the single state the execution summary
 * renders. The match arms are ordered by precedence: a workflow terminated
 * on a no-longer-dunnable invoice wins over the cancellations it causes
 * (terminating cancels the other branch's in-flight tasks); a completed send
 * wins over cancellation, because once the email has left, teardown of the
 * watcher branch says nothing about the reminder; a payment-terms skip likewise
 * wins over the teardown cancellations that follow it; and terminal states win
 * over in-flight ones.
 */
export const deriveReminderState = (
  tasks: ExecutionSummaryComponentProps['tasks']
): ReminderState => {
  const parsedTasks = tasks.map(task => ({
    task,
    parsed: parseTaskReferenceName(task.taskReferenceName)
  }))

  const findTask = (taskName: TaskName) =>
    parsedTasks.find(({ parsed }) => parsed.taskName === taskName)?.task

  const watcherTerminated =
    findTask(REMINDER_TASK_NAMES.invoiceResolutionTerminate)?.status ===
    'COMPLETED'
  const mainCheckTerminated =
    findTask(REMINDER_TASK_NAMES.mainValidForDunningCheckTerminate)?.status ===
    'COMPLETED'
  const postReviewCheckTerminated =
    findTask(REMINDER_TASK_NAMES.postReviewValidForDunningCheckTerminate)
      ?.status === 'COMPLETED'

  // Each terminate reads its own freshly fetched invoice: the send-time check off get_data_invoice,
  // the post-review check off its own re-fetch, and the resolution watcher off its own re-fetch.
  const terminatedInvoiceTask = match({
    watcherTerminated,
    mainCheckTerminated,
    postReviewCheckTerminated
  })
    .with({ mainCheckTerminated: true }, () =>
      findTask(REMINDER_TASK_NAMES.getDataInvoice)
    )
    .with({ postReviewCheckTerminated: true }, () =>
      findTask(REMINDER_TASK_NAMES.postReviewGetInvoice)
    )
    .with({ watcherTerminated: true }, () =>
      findTask(REMINDER_TASK_NAMES.invoiceResolutionGetInvoice)
    )
    .otherwise(() => undefined)
  const terminatedPaymentStatus = getInvoicePaymentStatus(terminatedInvoiceTask)

  // Don't consider the `invoiceResolutionWait` task when determining cancelled status
  // This task is marked as cancelled when Conductor tears down the workflow,
  // even after successfully completing a workflow
  const cancelled = hasCancelledTask(
    parsedTasks
      .filter(
        ({ parsed }) =>
          parsed.taskName !== REMINDER_TASK_NAMES.invoiceResolutionWait
      )
      .map(({ task }) => task)
  )

  const failed = hasFailedTask(tasks)

  const sendTask = parsedTasks.find(({ parsed }) =>
    SEND_TASK_NAMES.includes(parsed.taskName as TaskName)
  )?.task
  const sent = sendTask?.status === 'COMPLETED'
  const sending = Boolean(sendTask && isTaskInProgress(sendTask))

  // The payment-terms guard's completion anchor with no send task scheduled means the guard chose
  // SKIP: the reminder's send time fell on or before the day the invoice was sent, so nothing in
  // the SEND case ever ran. On the SEND path the anchor also completes, but only after the send
  // task exists, so `sent`/`sending` take precedence.
  const skipped =
    findTask(REMINDER_TASK_NAMES.paymentTermsCheckComplete)?.status ===
      'COMPLETED' && !sendTask

  const replacementFailureReviewTask = findTask(
    REMINDER_TASK_NAMES.replacementFailureManualReview
  )
  const inReplacementFailureReview = Boolean(
    replacementFailureReviewTask &&
      isTaskInProgress(replacementFailureReviewTask)
  )

  const manualReviewTask = findTask(REMINDER_TASK_NAMES.manualReview)
  const inReview = Boolean(
    manualReviewTask && isTaskInProgress(manualReviewTask)
  )

  const mainWaitTask = findTask(REMINDER_TASK_NAMES.mainWait)
  const waiting = Boolean(mainWaitTask && isTaskInProgress(mainWaitTask))

  return match({
    terminatedPaymentStatus,
    cancelled,
    failed,
    sent,
    sending,
    skipped,
    inReplacementFailureReview,
    inReview,
    waiting
  })
    .returnType<ReminderState>()
    .with({ terminatedPaymentStatus: 'PAID' }, () => ({
      type: 'INVOICE_PAID'
    }))
    .with({ terminatedPaymentStatus: 'UNCOLLECTIBLE' }, () => ({
      type: 'INVOICE_UNCOLLECTIBLE'
    }))
    .with({ sent: true }, () => ({ type: 'SENT' }))
    .with({ sending: true }, () => ({ type: 'SENDING' }))
    .with({ skipped: true }, () => ({ type: 'SKIPPED' }))
    .with({ cancelled: true }, () => ({ type: 'CANCELLED' }))
    .with({ failed: true }, () => ({ type: 'FAILED' }))
    .with({ inReplacementFailureReview: true }, () => ({
      type: 'IN_REVIEW',
      isReplacementFailure: true
    }))
    .with({ inReview: true }, () => ({
      type: 'IN_REVIEW',
      isReplacementFailure: false
    }))
    .with({ waiting: true }, () => ({
      type: 'WAITING',
      // Wait tasks carry their `until` timestamp directly on inputData,
      // unlike executor tasks which nest everything under `data`
      waitUntil: parseWaitUntil(
        (mainWaitTask?.inputData as Record<string, unknown>)?.until
      )
    }))
    .otherwise(() => ({ type: 'PREPARING' }))
}
